import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useEffect, useState } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";

export function NavMain({
  items,
  label,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    permission?: string;
    items?: {
      title: string;
      url: string;
      permission?: string;
      kind?: "label";
    }[];
  }[];
  label?: string;
}) {
  const location = useLocation();

  const isPathMatch = (url: string, pathname: string) => {
    if (!url || url === "#") {
      return false;
    }
    return pathname === url || pathname.startsWith(`${url}/`);
  };

  const activeItemIndex = items.findIndex((item) =>
    item.items?.some(
      (subItem) => subItem.kind !== "label" && isPathMatch(subItem.url, location.pathname)
    )
  );
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(activeItemIndex);

  useEffect(() => {
    setOpenItemIndex(activeItemIndex >= 0 ? activeItemIndex : null);
  }, [location.pathname, activeItemIndex]);

  return (
    <SidebarGroup>
      {label && <SidebarGroupLabel className="text-sm font-bold">{label}</SidebarGroupLabel>}
      <SidebarMenu>
        {items.map((item, itemIndex) =>
          !item.items ? (
            <SidebarMenuItem key={`${item.title}-${item.url}-${itemIndex}`}>
              <SidebarMenuButton asChild tooltip={item.title}>
                <Link to={item.url}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            // If has subitems, render as collapsible
            <Collapsible
              key={`${item.title}-${item.url}-${itemIndex}`}
              asChild
              open={openItemIndex === itemIndex}
              onOpenChange={(isOpen) => {
                setOpenItemIndex(isOpen ? itemIndex : null);
              }}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem, subItemIndex) => (
                      subItem.kind === "label" ? (
                        <li
                          key={`${subItem.title}-${subItemIndex}`}
                          className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/60"
                        >
                          {subItem.title}
                        </li>
                      ) : (
                        <SidebarMenuSubItem
                          key={`${subItem.title}-${subItem.url}-${subItemIndex}`}
                        >
                          <SidebarMenuSubButton asChild size="sm">
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
